import { I18n } from '@calendly/i18n';
import { BareButton } from '@calendly/ui/components/bare-button';
import { useViewportSize } from '@calendly/ui/hooks/viewport-size-context';
import { ReactNode, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useGetUserQuery } from 'common';
import { JoinAnOrgBanner } from 'components/home_bar';
import { AppBar } from 'features/app_shell/app_bar';
import { BrandRefreshBetaGlobalHeader } from 'features/app_shell/brand_refresh_beta/BrandRefreshBetaGlobalHeader';
import { BrandRefreshWelcomeGate } from 'features/app_shell/brand_refresh_beta/BrandRefreshWelcomeGate';
import { AppShellVerticalNav } from 'features/app_shell/main_nav/AppShellVerticalNav';
import { useAccountMenuPlanLabel } from 'features/app_shell/main_nav/useAccountMenuPlanLabel';
import { PretendModeShellBanner } from 'features/app_shell/pretend_mode';
import { isEnterpriseSelector } from 'features/billing';
import { LeftNavigationContext, leftNavigationSelector, setOpen } from 'features/left_navigation';
import { OrgConsolidationGlobalNotification } from 'features/organization_consolidation';
import { parseSettingsSectionFromSearch } from 'features/settings_modal';
import { currentOrganizationNameSelector } from 'selectors/current_organization';
import { AppShell } from './AppShell';
import { ShellBody } from './ShellBody';
const mainNavContainerCss = "m5wv367";
const mobileNavOverlayCss = "mlsr5mg";
const mobileNavDrawerCss = "m1bv2uoy";
const appLayoutRootCss = "a1edsoe8";
const globalBannerCss = "g1o1pj1i";
const appBarContainerCss = "a1j0fa9e";

/** Shared card shape for content and side panel — border-radius zeroed on mobile in one place. */
const bodyPanelCss = "b116t1cj";

/** Layout tree for `AppLayout` — only `children` is public; the rest is composed internally. */
interface AppLayoutProps {
  /** Routed page content (meets, home, etc.) inside the primary center panel. */
  children: ReactNode;
}

/**
 * Full-viewport **product refresh** shell: vertical main nav, top app bar, primary content column, optional side panel.
 *
 * Wraps content with `LeftNavigationContext` for hooks shared with legacy nav. Builds `navProfile` from the same
 * user/org signals as the legacy header menu so avatar and menus stay consistent.
 *
 * @param props.children - Rendered inside `CenterPanel` beneath global banners.
 */
export const AppLayout = ({
  children
}: AppLayoutProps) => {
  /* Same user fields as `DropDownUserMenu` (`features/header/drop-down-user-menu.js`) for Super Admin visibility. */
  const {
    data: {
      name,
      avatar_url: avatarSrc,
      avatar_letter: avatarLetter,
      is_super_admin: isSuperAdmin
    } = {}
  } = useGetUserQuery(undefined);
  const organizationName = useSelector(currentOrganizationNameSelector) ?? '';
  const isEnterprise = useSelector(isEnterpriseSelector);
  const planLabel = useAccountMenuPlanLabel();
  const {
    isSmallViewport
  } = useViewportSize();
  const dispatch = useDispatch();
  const {
    open: isMobileNavOpen
  } = useSelector<unknown, {
    open: boolean;
  }>(leftNavigationSelector);
  const location = useLocation();
  const {
    pathname
  } = location;

  /** Close the mobile nav drawer whenever the user navigates to a different path (normal in-app route changes). */
  useEffect(() => {
    dispatch(setOpen(false));
  }, [pathname, dispatch]);

  /**
   * Close the mobile nav drawer when the settings modal opens via URL (`?settings=…`, rebrand IA flow).
   *
   * That flow updates `location.search` only; `pathname` stays the same, so the pathname-only effect above does
   * not run. Leaving the drawer open would hide the settingsmodal behind the rail and leave `body` scroll locked.
   */
  useEffect(() => {
    if (!isSmallViewport || !isMobileNavOpen) return;
    if (parseSettingsSectionFromSearch(location.search) != null) {
      dispatch(setOpen(false));
    }
  }, [dispatch, isSmallViewport, isMobileNavOpen, location.search]);
  useEffect(() => {
    if (!isSmallViewport && isMobileNavOpen) dispatch(setOpen(false));
  }, [isSmallViewport, isMobileNavOpen, dispatch]);
  useEffect(() => {
    if (!isSmallViewport || !isMobileNavOpen) return undefined;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isSmallViewport, isMobileNavOpen]);
  const navProfile = useMemo(() => ({
    fullName: name ?? '',
    businessName: organizationName,
    planLabel,
    showAccountMenuUpgrade: !isEnterprise,
    avatarSrc,
    avatarLetter,
    isSuperAdmin: !!isSuperAdmin
  }), [avatarLetter, avatarSrc, isEnterprise, isSuperAdmin, name, organizationName, planLabel]);

  // Grid areas (`main-nav`, `app-bar`, `body`) are defined on `AppShell`.
  return <LeftNavigationContext>
      <div className={appLayoutRootCss}>
        <div className={globalBannerCss} id="shell-global-banner">
          <PretendModeShellBanner />
          <BrandRefreshBetaGlobalHeader />
        </div>
        <AppShell>
          {!isSmallViewport && <div className={mainNavContainerCss} id="shell-main-nav">
            <AppShellVerticalNav profile={navProfile} />
          </div>}
          {isSmallViewport && isMobileNavOpen && <>
            <BareButton className={mobileNavOverlayCss} aria-label={I18n.t('publisher.app_shell.app_bar.close')} onClick={() => dispatch(setOpen(false))} />
            <div className={mobileNavDrawerCss}>
              <AppShellVerticalNav profile={navProfile} fullExpandAndCollapse onClose={() => dispatch(setOpen(false))} />
            </div>
          </>}
          <div className={appBarContainerCss} id="shell-app-bar">
            <AppBar />
          </div>
          <ShellBody panelClassName={bodyPanelCss}>
            <OrgConsolidationGlobalNotification />
            <JoinAnOrgBanner />
            {children}
          </ShellBody>
        </AppShell>
      </div>
      <BrandRefreshWelcomeGate />
    </LeftNavigationContext>;
};

require("./AppLayout.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./AppLayout.tsx");